"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Magnetic from "./ui/Magnetic";

interface Node {
  id: string;
  label: string;
  tech: string;
  description: string;
  why: string;
  role: string;
  iconClass: string;
  details: string[];
}

const nodes: Node[] = [
  {
    id: "cloudflare",
    label: "Edge Network",
    tech: "Cloudflare CDN & Security",
    role: "DNS & Edge Cache Layer",
    description: "Global edge network caching static content, mitigating DDoS threats, managing SSL certificate configurations, and executing edge rules.",
    why: "Maximizes static export capabilities by serving pre-rendered portfolio pages directly from edge locations closest to recruiters, keeping TTFB under 30ms.",
    iconClass: "fi fi-rr-shield",
    details: ["Web Application Firewall (WAF)", "Anycast Global Routing", "Brotli Compression Enabled", "Page Rules Optimization"],
  },
  {
    id: "frontend",
    label: "Frontend Client",
    tech: "Next.js 16 / React 19",
    role: "User Interface & Static Export",
    description: "Highly performant UI with client-side hydration, prefetching routes, and responsive layouts utilizing Tailwind CSS 4 and Framer Motion.",
    why: "Allows robust static export compiling while preserving cutting-edge React features like concurrent rendering, hooks, and clean client states.",
    iconClass: "fi fi-rr-computer",
    details: ["Static HTML Export", "Geist & Space Grotesk Typography", "Framer Motion & GSAP animations", "Optimized WebP Images"],
  },
  {
    id: "gateway",
    label: "Reverse Proxy",
    tech: "Traefik / NGINX",
    role: "Gateway & Path Routing",
    description: "Orchestrates incoming route paths, terminates HTTPS handshakes, applies rate-limits, and redirects queries to appropriate microservices.",
    why: "Simplifies backend security boundaries and handles ingress traffic rules dynamically without modifying core application codes.",
    iconClass: "fi fi-rr-route",
    details: ["Dynamic Route Matching", "SSL Termination via Let's Encrypt", "IP Rate Limiting Middleware", "Path Redirections (/api/*)"],
  },
  {
    id: "api",
    label: "Core API Backend",
    tech: "Node.js / Express / TS",
    role: "Business Logic Engine",
    description: "TypeScript REST API hosting authentication middleware, data validation routers, database interactions, and integrations with third-party service gateways.",
    why: "Leverages Node's non-blocking I/O event loop to serve high volumes of requests with minimal computational resource overhead.",
    iconClass: "fi fi-rr-api",
    details: ["Express REST Endpoints", "JWT Auth Sanitization", "Prisma ORM Client", "Strict TypeScript Compilation"],
  },
  {
    id: "redis",
    label: "In-Memory Cache",
    tech: "Redis Key-Value Cache",
    role: "Database Caching & Rate-Limiter",
    description: "Stores session variables, temporarily caches frequent database query results, and implements leaky-bucket token pools to limit user request bounds.",
    why: "Bypasses slow disk-based database reads, resolving repeated client queries instantly in under 5ms.",
    iconClass: "fi fi-rr-bolt",
    details: ["Cache-Aside Query Management", "Sub-Millisecond Read Latency", "TTL Auto-Eviction Settings", "Websocket Pub/Sub Coordination"],
  },
  {
    id: "database",
    label: "Database Engine",
    tech: "MySQL Database",
    role: "Persistent Relational Storage",
    description: "Structured storage system maintaining data integrity via strict schemas, foreign keys constraints, and transactional execution rules.",
    why: "Guarantees ACID transactions for multi-tenant app architectures, preventing records collision or partial writes failures.",
    iconClass: "fi fi-rr-database",
    details: ["ACID Compliance Guarantees", "Structured Entity Relations", "Query Execution Plan Indexing", "Connection Pooling Managers"],
  },
  {
    id: "docker",
    label: "Runtime Platform",
    tech: "Docker Containers",
    role: "Environment Isolation Unit",
    description: "Encapsulates application dependencies, configuration files, and runtimes inside isolated sandbox images run uniformly across environments.",
    why: "Removes environment discrepancies between local environments and cloud instances, facilitating automated Kubernetes deploys.",
    iconClass: "fi fi-brands-docker",
    details: ["Multi-Stage Dockerfiles", "Minimal Alpine Base Runtimes", "Environment Configurations Injection", "Compose Staging Pipelines"],
  },
];

export default function Architecture() {
  const [selectedNode, setSelectedNode] = useState<Node>(nodes[0]);

  return (
    <section id="architecture" className="section-pad relative z-10 bg-black/40 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-6 block">Infrastructure</span>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-6 font-[var(--font-poppins)] tracking-tighter">
            System <span className="gradient-text">Architecture</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg font-medium">
            An interactive representation of my full stack production setup. Select any node to explore technical justifications.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Diagrams Left Column */}
          <div className="lg:col-span-7 flex flex-col items-center">
            <div className="relative w-full max-w-lg bg-white/[0.02] border border-white/5 rounded-[2.5rem] p-8 md:p-12 overflow-hidden flex flex-col gap-6">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-indigo-500/5 blur-[80px] pointer-events-none rounded-full" />
              
              {nodes.map((node, i) => {
                const isSelected = selectedNode.id === node.id;
                return (
                  <div key={node.id} className="flex flex-col items-center w-full">
                    {/* Interactive Node Button */}
                    <Magnetic>
                      <button
                        onClick={() => setSelectedNode(node)}
                        className={`w-full flex items-center justify-between p-4 rounded-2xl border text-left transition-all duration-300 relative z-10 ${
                          isSelected
                            ? "bg-indigo-500/10 border-indigo-400/50 shadow-[0_0_20px_rgba(99,102,241,0.2)]"
                            : "bg-white/[0.02] border-white/5 hover:border-white/20 hover:bg-white/[0.04]"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300 ${
                            isSelected ? "bg-indigo-500/20 text-indigo-300" : "bg-white/5 text-gray-400"
                          }`}>
                            <i className={`${node.iconClass} text-base`} />
                          </div>
                          <div>
                            <h3 className="text-white text-sm font-black uppercase tracking-wider">{node.label}</h3>
                            <span className="text-[10px] text-gray-400 font-mono">{node.tech}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[8px] px-2 py-0.5 rounded font-mono uppercase ${
                            isSelected ? "bg-indigo-400/20 text-indigo-300" : "bg-white/5 text-gray-400"
                          }`}>
                            {node.id === "cloudflare" || node.id === "frontend" ? "Client Side" : "Server/Infra"}
                          </span>
                        </div>
                      </button>
                    </Magnetic>

                    {/* Flow arrow down */}
                    {i < nodes.length - 1 && (
                      <div className="my-2 text-indigo-500/30 flex flex-col items-center">
                        <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Details Right Column */}
          <div className="lg:col-span-5 relative z-10 min-h-[500px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedNode.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="bg-white/[0.03] border border-white/10 rounded-[2.5rem] p-8 md:p-10 shadow-2xl relative overflow-hidden h-full flex flex-col"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none -mr-32 -mt-32" />
                
                {/* Section Tag */}
                <span className="text-[10px] font-bold tracking-widest text-indigo-400 uppercase mb-2 block font-mono">
                  {selectedNode.role}
                </span>

                <h3 className="text-3xl font-black text-white mb-1 font-[var(--font-poppins)] tracking-tight">
                  {selectedNode.label}
                </h3>
                <p className="text-sm font-semibold text-gray-400 font-mono mb-6">
                  {selectedNode.tech}
                </p>

                <h4 className="text-xs font-black uppercase text-indigo-300 mb-2 tracking-wider">How it works:</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-6 font-medium">
                  {selectedNode.description}
                </p>

                <div className="h-px bg-white/5 my-4" />

                <h4 className="text-xs font-black uppercase text-indigo-300 mb-2 tracking-wider">Why selection:</h4>
                <p className="text-gray-300 text-sm leading-relaxed mb-6 font-medium italic bg-indigo-500/5 p-4 rounded-xl border border-indigo-500/10">
                  &ldquo;{selectedNode.why}&rdquo;
                </p>

                <h4 className="text-xs font-black uppercase text-indigo-300 mb-3 tracking-wider">Key Configurations:</h4>
                <ul className="space-y-2 mt-auto">
                  {selectedNode.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-xs text-gray-400 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
